import { useEffect, useRef, useState } from "react";
import { faTag, faTimes } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import {
  CartApplyPromoCodeErrorData,
  ClientCartDetail,
  PromoCodeCartError,
} from "common/types/carts";
import { IPromoCode } from "common/types/promotions";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";
import { useLocation } from "common/router/hooks";
import { useAuthentication } from "common/auth/hooks";
import { FetchError } from "common/fetch/errors";
import { toDollars } from "common/utils/formatting";

import BaseInput from "client/common/components/inputs/BaseInput";
import Form from "client/common/components/forms/Form";
import Alert from "client/common/components/content/Alert";
import Button from "client/common/components/buttons/Button";
import Banner from "client/common/components/content/Banner";
import alertIcons from "client/common/constants/alertIcons";
import TextButton from "client/common/components/buttons/TextButton";
import { urls } from "client/routes";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import { useCartApplyPromoCodeMutation, useCartUnapplyPromoCodeMutation } from "../hooks";

import styles from "../styles/PromoCodes.module.less";

const PromoCode = ({ cartId, promoCode }: { cartId: number; promoCode: IPromoCode }) => {
  const { mutate: unapplyPromoCode, isLoading } = useCartUnapplyPromoCodeMutation({
    cartId,
    promoId: promoCode.id,
  });

  return (
    <Banner variant="promotion" className={cx(styles.promoCode, styles.banner)}>
      <div className={styles.bannerLeft}>
        <Icon icon={faTag} className={styles.icon} fixedWidth={true} />
        <Banner.Text label={promoCode.code} />
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <button className={styles.close} onClick={() => unapplyPromoCode()} type="button">
          <Icon icon={faTimes} />
        </button>
      )}
    </Banner>
  );
};

const PromoCodes = ({ cart }: { cart: ClientCartDetail }) => {
  const { close } = useCurrentDrawer();
  const inputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated } = useAuthentication();
  const location = useLocation();
  const [isEntering, setIsEntering] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const {
    mutate: applyPromoCode,
    data,
    isLoading: isPromoLoading,
    error,
    isSuccess,
    reset,
  } = useCartApplyPromoCodeMutation({
    cartId: cart.id,
    onSuccess: () => {
      setPromoCode("");
      setIsEntering(false);
    },
  });

  // Reset the error display when the input is cleared
  useEffect(() => {
    if (error && !promoCode) {
      reset();
    }
  }, [error, promoCode, reset]);

  const onApplyPromoCode = () => {
    applyPromoCode({ promo_code: promoCode });
  };

  const promoCodeCartErrorDisplay = (
    promoCodeError: FetchError<void, CartApplyPromoCodeErrorData>,
  ) => {
    switch (promoCodeError.data?.kind) {
      case PromoCodeCartError.DOES_NOT_EXIST:
        return "Promo code does not exist.";
      case PromoCodeCartError.INACTIVE:
        return "Promo code is expired or inactive.";
      case PromoCodeCartError.ONETIME_CONSUMED:
        return "Promo code already used. Limit one use per customer.";
      case PromoCodeCartError.FIRST_AUTOSHIP_ELIGIBILITY_CONSUMED:
        return "Promo code is valid for first-time autoship orders only. You have previously purchased the eligible item(s) on autoship, so this promo cannot be redeemed.";
      case PromoCodeCartError.AUTOSHIP_PROMO_BUT_ITEMS_NON_AUTOSHIP:
        return "Promo code is valid for first-time autoship orders only. The eligible item(s) in your cart are currently configured for one-time purchases.";
      case PromoCodeCartError.AUTOSHIP_PROMO_WITH_LOGGED_OUT_CART:
        return "Promo code is valid for first-time autoship orders only. Please login to confirm eligibility.";
      case PromoCodeCartError.INVALID_ITEMS_FOR_PROMO:
        return "The item(s) in your cart are not eligible for this promo code. Please review your selections.";
      case PromoCodeCartError.MIN_ORDER_SUBTOTAL_NOT_MET:
        if (promoCodeError.data?.amount_needed) {
          return `Your cart does not meet the minimum purchase requirement for this promo code. Add ${toDollars(promoCodeError.data.amount_needed)} more in eligible items to apply this promo code.`;
        }
        return "Your cart does not meet the minimum purchase requirement for this promo code.";
      case PromoCodeCartError.NOT_APPLICABLE:
        return isAuthenticated ? (
          "Promotion is not applicable."
        ) : (
          <>
            Please{" "}
            <TextButton
              dest={urls.accounts.login({
                search: { next: location.current.href },
              })}
              onClick={close}
              hasUnderline={true}
            >
              log in
            </TextButton>{" "}
            or{" "}
            <TextButton
              dest={urls.accounts.signup({
                search: { next: location.current.href },
              })}
              onClick={close}
              hasUnderline={true}
            >
              create an account
            </TextButton>{" "}
            first to verify promo eligibility.
          </>
        );
      case PromoCodeCartError.GLOBALLY_SINGLE_USE_CONSUMED:
        return "This code has already been redeemed.";
      case PromoCodeCartError.ALREADY_APPLIED:
        return "Promo code is already applied!";
      default:
        return promoCodeError.detail;
    }
  };

  const canSubmit = !isPromoLoading && !!promoCode;

  // Focus the input once it appears
  useEffect(() => {
    if (isEntering) {
      inputRef.current?.focus();
    }
  }, [isEntering]);

  return (
    <Form canSubmit={canSubmit} onSubmit={() => onApplyPromoCode()}>
      <div className={styles.container}>
        {isEntering ? (
          <BaseInput
            value={promoCode}
            onChange={setPromoCode}
            errors={[
              ...(error
                ? [
                    {
                      key: "promo code",
                      content: <div>{promoCodeCartErrorDisplay(error)}</div>,
                    },
                  ]
                : []),
            ]}
            renderInputDisplay={({ onChange, preIcon, status, ...inputProps }) => (
              <BaseInput.AnimationContainer label="Promo Code">
                <div className={styles.input}>
                  <input
                    {...inputProps}
                    placeholder="" // Disable placeholders since the label sticks there when value is empty
                    type="text"
                    onChange={(event) => onChange(event.target.value)}
                    ref={inputRef}
                  />
                  <div className={`${styles.button} ${promoCode ? styles.active : ""}`}>
                    <Button isLoading={isPromoLoading} size="xs" type="submit">
                      Apply
                    </Button>
                  </div>
                </div>
              </BaseInput.AnimationContainer>
            )}
          />
        ) : (
          <Banner.LinkButton onClick={() => setIsEntering(true)} className={styles.banner}>
            <div className={styles.bannerLeft}>
              <Icon icon={alertIcons.info} className={styles.icon} fixedWidth={true} />
              <Banner.Text
                label={
                  cart.promo_codes.length === 0 ? "Have a promo code?" : "Have another promo code?"
                }
              />
            </div>
          </Banner.LinkButton>
        )}
        {isSuccess && data?.message && <Alert variant="warning">{data.message}</Alert>}
        {!!cart.promo_codes.length && (
          <div className={styles.appliedCodes}>
            {cart.promo_codes.map((code) => (
              <PromoCode key={code.id} cartId={cart.id} promoCode={code} />
            ))}
          </div>
        )}
      </div>
    </Form>
  );
};

export default PromoCodes;
